// 🔧 vote_builder.js
// ✅ Purpose: Vote and Detailed Scorecard evaluation builder for the LGV Demo Engine
// ✅ Features:
// - Preserves existing standard demo vote generation
// - Detects Detailed Scorecard Experiences from real scoring metadata
// - Uses scoringResult.createdFactors produced by Scoring Builder
// - Uses industry scoring configuration only as generic declarative metadata
// - Supports manifest-defined evaluation volume for scored Experiences
// - Creates deterministic user + entry vote IDs for Points voting
// - Prevents duplicate scorecard evaluations for the same voter + entry
// - Persists canonical Points vote documents consumed by LGV results pages
// - Leaves non-scorecard Experience behavior unchanged
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
} from "firebase/firestore";

import {
  db,
} from "../../firebase";

import {
  logger,
} from "../../utils/logger";

import {
  getDemoSizeConfig,
} from "../config/demo_sizes";

import {
  generateDemoVotes,
  generateDetailedScorecardDemoVotes,
  hasScoredHighlights,
} from "../generators/vote_generator";

import {
  buildDemoVoteDoc,
  buildDetailedScorecardDemoVoteDoc,
} from "../templates/vote_defaults";
import { commitBatchedWrites } from "../repositories/batched_writes";

/**
 * ------------------------------------------------------------
 * RESOLVE SCORECARD EVALUATION COUNT
 * ------------------------------------------------------------
 *
 * Existing non-scorecard demos continue to use Demo Size.
 *
 * Scored Experiences may declare their preferred realistic
 * evaluation volume through the industry manifest defaults.
 *
 * Example:
 * SAP declares 250 evaluations even if Demo Size remains Small.
 */
const resolveDetailedScorecardVoteCount = ({
  industryTemplate,
  sizeConfig,
} = {}) => {
  const fixedVoteCount =
    Number(
      industryTemplate
        ?.fixedVoteCount ||
      0
    );

  if (
    Number.isInteger(
      fixedVoteCount
    ) &&
    fixedVoteCount > 0
  ) {
    return fixedVoteCount;
  }

  return Number(
    sizeConfig?.voteCount ||
    0
  );
};

/**
 * ------------------------------------------------------------
 * CREATE STANDARD DEMO VOTES
 * ------------------------------------------------------------
 * Pre-existing behavior is intentionally isolated here so scored
 * Experience support does not alter ordinary Demo Engine voting.
 */
const createStandardDemoVotes = async ({
  eventId,
  generatedVotes,
} = {}) => {
  const createdVotes = [];
  const operations = [];

  for (
    const generatedVote
    of generatedVotes
  ) {
    const voteDoc =
      buildDemoVoteDoc({
        generatedVote,
        eventId,
      });

    const voteRef = doc(collection(db, "events", eventId, "votes"));

    operations.push((batch) => batch.set(voteRef, voteDoc));

    createdVotes.push({
      id:
        voteRef.id,

      voterId:
        generatedVote.voterId,

      entryId:
        generatedVote.entryId,
    });
  }

  await commitBatchedWrites({ operations });

  return createdVotes;
};

/**
 * ------------------------------------------------------------
 * CREATE DETAILED SCORECARD DEMO EVALUATIONS
 * ------------------------------------------------------------
 *
 * Production Points voting uses:
 *
 *   voteId = `${uid}_${entry.id}`
 *
 * We mirror that uniqueness contract so a synthetic judge cannot
 * produce duplicate evaluations for the same entry.
 */
const createDetailedScorecardDemoVotes = async ({
  eventId,
  generatedVotes,
} = {}) => {
  const createdVotes = [];
  const operations = [];

  for (
    const generatedVote
    of generatedVotes
  ) {
    const voteId =
      `${generatedVote.voterId}_${generatedVote.entryId}`;

    const voteRef =
      doc(
        db,
        "events",
        eventId,
        "votes",
        voteId
      );

    const voteDoc =
      buildDetailedScorecardDemoVoteDoc({
        generatedVote,
        eventId,
      });

    operations.push((batch) => batch.set(voteRef, voteDoc, { merge: true }));

    createdVotes.push({
      id:
        voteId,

      voterId:
        generatedVote.voterId,

      entryId:
        generatedVote.entryId,

      totalPoints:
        generatedVote.totalPoints,

      maxBallotPoints:
        generatedVote.maxBallotPoints,
    });
  }

  await commitBatchedWrites({ operations });

  return createdVotes;
};

/**
 * ------------------------------------------------------------
 * BUILD VOTES
 * ------------------------------------------------------------
 */
export const buildVotes = async ({
  eventResult,
  entryResult,
  userResult,
  demoConfig,
  scoringResult,
  industryTemplate,
} = {}) => {
  if (
    !eventResult?.eventId
  ) {
    throw new Error(
      "Vote Builder requires an eventId."
    );
  }

  const eventId =
    eventResult.eventId;

  const users =
    userResult?.createdUsers ||
    [];


  //
  // SAP Detailed Scorecard uses a fixed judge pool.
  // Other demos continue using all generated users.
  //
  const evaluatorUsers =
    users.slice(
      0,
      Number(
        industryTemplate
          ?.scoring
          ?.judgeCount ||
        users.length
      )
    );

  const entries =
    entryResult?.createdEntries ||
    [];

  const scoringFactors =
    scoringResult?.createdFactors ||
    [];

  const sizeConfig =
    getDemoSizeConfig(
      demoConfig?.size
    );

  const isDetailedScorecard =
    hasScoredHighlights(
      scoringFactors
    );

  // =========================================================
  // DETAILED SCORECARD EXPERIENCE
  // =========================================================

  if (
    isDetailedScorecard
  ) {
    const voteCount =
      resolveDetailedScorecardVoteCount({
        industryTemplate,
        sizeConfig,
      });

    const configuredMaximum =
      Number(
        industryTemplate
          ?.scoring
          ?.maxBallotPoints ||
        0
      );

    const generatedVotes =
      generateDetailedScorecardDemoVotes({
        users: evaluatorUsers,
        entries,
        scoringFactors,

        voteCount,

        maxBallotPoints:
          configuredMaximum ||
          null,
      });

    logger.log(
      "🧪 Vote Builder creating Detailed Scorecard demo evaluations:",
      {
        value: {
          eventId,

          requestedEvaluationCount:
            voteCount,

          createdEvaluationPlan:
            generatedVotes.length,

          scoringFactorCount:
            scoringFactors.length,

          entryCount:
            entries.length,

          syntheticEvaluatorCount:
            evaluatorUsers.length,
        },
      }
    );

    const createdVotes =
      await createDetailedScorecardDemoVotes({
        eventId,
        generatedVotes,
      });

    logger.log(
      "✅ Vote Builder completed Detailed Scorecard demo evaluations:",
      {
        value: {
          createdVoteCount:
            createdVotes.length,
        },
      }
    );

    return {
      builder:
        "vote_builder",

      mode:
        "detailed_scorecard",

      ready:
        true,

      createdCount:
        createdVotes.length,

      createdVotes,

      message:
        "Vote Builder created Detailed Scorecard demo evaluations.",
    };
  }

  // =========================================================
  // STANDARD DEMO VOTING
  // =========================================================
  // Preserve established behavior exactly for Experiences that
  // have no scored Detailed Scorecard metadata.
  // =========================================================

  const demoCreator = users.find((user) => user.demoCreator === true);
  const syntheticUsers = demoCreator
    ? users.filter((user) => user.id !== demoCreator.id)
    : users;

  const generatedVotes =
    generateDemoVotes({
      users: syntheticUsers,
      entries,

      voteCount:
        sizeConfig.voteCount,
    });

  // Give the authenticated demo creator a complete example ballot. Synthetic
  // voters still provide the broader results distribution, while the person
  // testing the Experience can immediately inspect one vote per category.
  if (demoCreator) {
    const firstEntryByCategory = new Map();

    entries.forEach((entry) => {
      const categoryId = String(entry.categoryId || "");

      if (categoryId && !firstEntryByCategory.has(categoryId)) {
        firstEntryByCategory.set(categoryId, entry);
      }
    });

    firstEntryByCategory.forEach((entry) => {
      generatedVotes.push({
        voterId: demoCreator.id,
        voterName: demoCreator.displayName,
        entryId: entry.id,
        entryTitle: entry.title,
        categoryId: entry.categoryId,
        categoryName: entry.categoryName,
        ballotPosition: 1,
      });
    });
  }

  logger.log(
    "🧱 Vote Builder creating Firestore votes:",
    {
      value: {
        eventId,

        plannedVoteCount:
          generatedVotes.length,
      },
    }
  );

  const createdVotes =
    await createStandardDemoVotes({
      eventId,
      generatedVotes,
    });

  logger.log(
    "✅ Vote Builder completed:",
    {
      value: {
        createdVoteCount:
          createdVotes.length,
      },
    }
  );

  return {
    builder:
      "vote_builder",

    mode:
      "standard",

    ready:
      true,

    createdCount:
      createdVotes.length,

    createdVotes,

    message:
      "Vote Builder created demo votes.",
  };
};

/**
 * ------------------------------------------------------------
 * BUILD VOTE SHELL
 * ------------------------------------------------------------
 *
 * Pipeline context already contains scoringResult and
 * industryTemplate. This function now forwards both into the
 * generic Vote Builder.
 */
export const buildVoteShell = async ({
  eventResult,
  entryResult,
  userResult,
  demoConfig,
  scoringResult,
  industryTemplate,
} = {}) => {
  const sizeConfig =
    getDemoSizeConfig(
      demoConfig?.size
    );

  const scoringFactors =
    scoringResult?.createdFactors ||
    [];

  const isDetailedScorecard =
    hasScoredHighlights(
      scoringFactors
    );

  const plannedCount =
    isDetailedScorecard
      ? resolveDetailedScorecardVoteCount({
          industryTemplate,
          sizeConfig,
        })
      : sizeConfig.voteCount;

  if (
    !eventResult?.eventId ||
    !entryResult?.createdEntries?.length ||
    !userResult?.createdUsers?.length
  ) {
    return {
      builder:
        "vote_builder",

      ready:
        false,

      plannedCount,

      message:
        "Vote Builder requires event, entries and users before Firestore creation.",
    };
  }

  return buildVotes({
    eventResult,
    entryResult,
    userResult,
    demoConfig,
    scoringResult,
    industryTemplate,
  });
};
